import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { ApiError } from './models/ApiError.interface';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TelemetryService } from '../../shared/services/telemetry.service';

@Injectable()
export class ApiErrorInterceptor implements HttpInterceptor {
  constructor(
    private snackBar: MatSnackBar,
    private telemetry: TelemetryService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const apiError = error.error as ApiError | undefined;

        const code = (apiError as any)?.code ?? (apiError as any)?.Code;
        const message = (apiError as any)?.message ?? (apiError as any)?.Message;

        this.trackApiErrorTelemetry(error, req, code);

        if (code) {
          switch (code) {
            case 'Unauthorized':
              this.snackBar.open('Du er ikke logget inn', 'Lukk', { duration: 5000 });
              break;

            case 'ApimTimeout':
              this.snackBar.open('Teknisk feil ved henting av data. Prøv igjen senere.', 'Lukk', { duration: 5000 });
              break;

            case 'ApimUnauthorized':
              this.snackBar.open('Du har ikke tilgang til denne tjenesten.', 'Lukk', { duration: 5000 });
              break;

            case 'ApimError':
              this.snackBar.open('Teknisk feil ved henting av data. Prøv igjen senere.', 'Lukk', { duration: 5000 });
              break;

            case 'ValidationError':
              this.snackBar.open(message || 'Ugyldig data i forespørselen.', 'Lukk', { duration: 5000 });
              break;
          }
        }

        return throwError(() => error);
      })
    );
  }

  private trackApiErrorTelemetry(
    error: HttpErrorResponse,
    req: HttpRequest<any>,
    code: string | undefined
  ): void {
    const url = this.sanitizeUrl(req.urlWithParams || req.url);
    const statusCode = String(error.status);
    const properties = {
      url,
      method: req.method,
      statusCode,
      statusGroup: this.resolveStatusGroup(error.status),
      errorCode: code ?? 'Unknown',
    };

    if (code === 'Unauthorized' || code === 'ApimUnauthorized') {
      this.telemetry.trackEvent('Api.Unauthorized', properties);
      return;
    }

    if (this.isExpectedClientError(error.status, code)) {
      this.telemetry.trackEvent('Api.RequestFailedExpected', properties);
      return;
    }

    this.telemetry.trackException(new Error('Api request failed'), properties);
  }

  private isExpectedClientError(status: number, code: string | undefined): boolean {
    if (code === 'ValidationError') {
      return true;
    }

    return status === 400 || status === 404 || status === 409 || status === 422;
  }

  private resolveStatusGroup(status: number): string {
    if (status >= 500) return '5xx';
    if (status >= 400) return '4xx';
    if (status >= 300) return '3xx';
    if (status >= 200) return '2xx';
    return 'unknown';
  }

  private sanitizeUrl(url: string): string {
    try {
      const parsed = new URL(url, window.location.origin);
      return `${parsed.origin}${parsed.pathname}`;
    } catch {
      const [withoutHash] = url.split('#');
      return withoutHash.split('?')[0] ?? url;
    }
  }
}
